import grapesjs from 'grapesjs';
import { grapesJsBlocks } from './blocks/blocks';
import commandsManager from './commands/commandsManager';
import { loadCanvasCss } from './components/canvas-css';
import loadTraits from './components/canvas-traits';
import { useClient } from '../../react-hooks/useClient';
import codeEditor from './plugins/grapesjs-component-code-editor';
import './plugins/grapesjs-component-code-editor/grapesjs-component-code-editor.min.css';
import panels from './panels/panels';
import cssParser from 'grapesjs-parser-postcss';
import { createMedia } from '../utils/file';
import {
    loadBrandColors,
    loadMenusList,
    loadPagesList,
} from './services/grapesDataLoader';
import { applyGrapesRteToolbarStyles, getRichTextToolbarActions } from './services/grapejsRteToolbar';
import { initBlockSearch } from './services/blockSearch';
import { configDeviceList } from './config/devicesList';
import { migrateLegacyBreakpointsInProject } from './config/legacyDeviceMigration';
import { initCssVariables } from './cssVariables';
import autosaveStore from './services/autosaveStore';
import { confirm as confirmDialog } from '../../dialog/confirm';
import * as _ from 'lodash';

let editor = null;

export const loadGrapeJsEditor = async ({
    container,
    id,
    modelId,
    dynamicBlockConfig,
    website,
    data,
    onLoadingChange = () => { },
}) => {
    onLoadingChange(true);
    try {
        const client = useClient();
        const loadProject = async () => {
            try {
                const data = await client.get(
                    'grapes-editor/content/' + modelId + '/' + id,
                    false,
                    {
                        cache: 'no-store',
                    }
                );
                return data;
            } catch (error) {
                console.error('API Load Error:', error);
                return null;
            }
        };
        let  cssVariables = {};
        try{
            cssVariables = await client.get(
                'grapes-editor/corporate-design/css-variables/' + website?.id,
                false,
                {
                    cache: 'no-store',
                }
            );
        } catch(e){

        }

        const defaultProject = {
            pages: [
                {
                    name: 'Home',
                    component: `<section data-gjs-type="section" class="section sio-section">
                                        <div  class="container mx-auto" data-gjs-type="default"></div></section>`,
                },
            ],
        };

        const onEditorLoad = async () => {
            autosaveStore.prune();
            let project = await loadProject();
            if (!project?.pages || Object.keys(project).length === 0) {
                project = null;
            }
            const localEntry = autosaveStore.read(modelId, id);
            if (localEntry && localEntry.data != null) {
                const sameAsServer =
                    project != null && _.isEqual(localEntry.data, project);

                if (sameAsServer) {
                    autosaveStore.remove(modelId, id);
                } else {
                    const recover = await confirmDialog(
                        'Ungespeicherte Änderungen wiederherstellen?',
                        'Beim letzten Bearbeiten dieser Seite wurden Änderungen nicht gespeichert. Möchten Sie diese wiederherstellen?'
                    ).then(
                        () => true,
                        () => false
                    );

                    if (recover) {
                        return migrateLegacyBreakpointsInProject(localEntry.data);
                    }
                    autosaveStore.remove(modelId, id);
                }
            }
            const projectToLoad = project || defaultProject || {
                pages: [
                    {
                        name: 'Home',
                        component: `<section data-gjs-type="section" class="section sio-section">
                                        <div  class="container mx-auto" data-gjs-type="default"></div></section>`,
                    },
                ],
            };
            const migratedProject = migrateLegacyBreakpointsInProject(projectToLoad);
            autosaveStore.write(modelId, id, migratedProject);
            return migratedProject;
        };

        const pagesList = (await loadPagesList(website?.id)) || [];

        const hybridStoragePlugin = (editor) => {
            editor.Storage.add('hybrid', {
                async load() {
                    return await onEditorLoad();
                },

                async store(data) {
                    autosaveStore.write(modelId, id, data);
                    return true;
                },
            });
        };
        const menusList = (await loadMenusList(website?.id)) || [];
        if (editor) {
            editor.destroy();
        }
        applyGrapesRteToolbarStyles();
        const editorRef = { current: null };
        editor = grapesjs.init({
            container: container,
            height: '100vh',
            plugins: [hybridStoragePlugin, codeEditor, cssParser],
            richTextEditor: {
                actions: getRichTextToolbarActions(editorRef),
            },
            fromElement: true,
            showOffsets: true,
            selectorManager: { componentFirst: true },
            storageManager: {
                type: 'hybrid',
                autosave: true,
                stepsBeforeSave: 1,
                options: {
                    hybrid: {},
                },
            },
            assetManager: {
                upload: true,
                custom: false,
                embedAsBase64: false,
                uploadFile: function (e) {
                    const files = e.dataTransfer
                        ? e.dataTransfer.files
                        : e.target.files;
                    for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        createMedia(file).then((data) => {
                            editor.AssetManager.add({ src: data.source_url });
                        });
                    }
                },
            },
            canvas: {
                styles: [],
                scripts: ['https://cdn.tailwindcss.com'],
            },
            colorPicker: {
                palette: await loadBrandColors(website?.id),
            },
            deviceManager: {
                devices: configDeviceList.devices,
                defaults: configDeviceList.default,
            },
        });
        editorRef.current = editor;

        loadTraits(editor);
        commandsManager(editor);
        loadCanvasCss({ editor, website });
        grapesJsBlocks(
            editor,
            dynamicBlockConfig,
            website?.id,
            menusList,
            pagesList,
            data
        );
        panels(editor);
        initBlockSearch(editor);
        initCssVariables(editor, { variables: cssVariables || [] });

        return editor;
    } finally {
        onLoadingChange(false);
    }
};
